import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { Link, useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { PasswordStrengthMeter } from "@/components/PasswordStrengthMeter";
import { checkBreachedPassword } from "@/lib/hibpCheck";
import { SAGE } from "@shared/brand";
import { AlertTriangle } from "lucide-react";

const registerAdvisorSchema = z.object({
  fullName: z.string().min(2, "Full name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  companyName: z.string().min(1, "Company name is required"),
  profession: z.enum(["financial_planner", "estate_lawyer", "trust_company", "accountant", "other"]),
  phoneNumber: z.string().optional(),
  password: z.string()
    .min(10, "Password must be at least 10 characters")
    .refine(
      p => /[A-Z]/.test(p) && /[a-z]/.test(p) && /[0-9]/.test(p) && /[^A-Za-z0-9]/.test(p),
      "Password must include uppercase, lowercase, number, and special character"
    ),
  confirmPassword: z.string(),
  tosAccepted: z.boolean().refine(v => v === true, "You must accept the Terms of Service"),
}).refine(d => d.password === d.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type FormData = z.infer<typeof registerAdvisorSchema>;

const PROFESSION_LABELS: Record<string, string> = {
  financial_planner: "Financial Planner",
  estate_lawyer: "Estate Lawyer",
  trust_company: "Trust Company",
  accountant: "Accountant",
  other: "Other",
};

export default function RegisterAdvisor() {
  const [isLoading, setIsLoading] = useState(false);
  const [breachCount, setBreachCount] = useState<number | null>(null);
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const form = useForm<FormData>({
    resolver: zodResolver(registerAdvisorSchema),
    defaultValues: {
      fullName: "",
      email: "",
      companyName: "",
      profession: "financial_planner",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
      tosAccepted: false,
    },
  });

  const watchedPassword = form.watch("password");

  // D-170.6: converted from a raw apiRequest. Class A — creates an advisor account, mints a
  // discount code, and emails it. `users.email` is UNIQUE so the account cannot duplicate; the
  // double-submit failure is a second attempt losing the race and reporting "Email already
  // registered" to the person who just successfully registered.
  const registerFlight = useSingleFlight();
  const registerMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const res = await apiRequest("POST", "/api/auth/register-advisor", data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Registration failed");
      }
      return await res.json();
    },
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      const result = await registerFlight.run(() => registerMutation.mutateAsync(data));
      if (!result) return; // duplicate fire
      queryClient.clear();
      toast({ title: "Welcome!", description: "Your advisor portal is ready. Check your email for your discount code." });
      setLocation("/dashboard");
    } catch (error: any) {
      toast({ title: "Registration failed", description: error.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4 bg-background">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>Advisor Partner Registration</CardTitle>
          <CardDescription>
            Create a free advisor account — invite clients, track their estate planning progress,
            and share your referral link. For financial planners, estate lawyers, accountants,
            and trust companies.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField control={form.control} name="fullName" render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Full Name</FormLabel>
                    <FormControl><Input placeholder="Jane Smith" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="email" render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Email</FormLabel>
                    <FormControl><Input type="email" placeholder="jane@smithfp.com" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="companyName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name</FormLabel>
                    <FormControl><Input placeholder="Smith Financial Planning" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="profession" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Profession</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger><SelectValue placeholder="Select profession" /></SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(PROFESSION_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="phoneNumber" render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Phone <span className="text-muted-foreground">(optional)</span></FormLabel>
                    <FormControl><Input type="tel" placeholder="+1 416 555 1234" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="password" render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        {...field}
                        onBlur={async (e) => {
                          field.onBlur();
                          const pwd = e.target.value;
                          if (pwd.length >= 10) {
                            const count = await checkBreachedPassword(pwd);
                            setBreachCount(count);
                          } else {
                            setBreachCount(null);
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                    <PasswordStrengthMeter password={watchedPassword} />
                    {breachCount !== null && breachCount > 0 && (
                      <p className="text-xs mt-1" style={{ color: "#C53030" }}>
                        <AlertTriangle className="h-3.5 w-3.5 inline-block shrink-0 align-[-2px]" aria-hidden="true" /> This password has appeared in {breachCount.toLocaleString()} data breach{breachCount === 1 ? "" : "es"}. Choose a different password.
                      </p>
                    )}
                  </FormItem>
                )} />
                <FormField control={form.control} name="confirmPassword" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl><Input type="password" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

              <FormField control={form.control} name="tosAccepted" render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      I agree to the{" "}
                      <a
                        href="/terms-of-service"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                        style={{ color: SAGE[600] }}
                      >
                        Terms of Service
                      </a>
                      {" "}and{" "}
                      <a
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                        style={{ color: SAGE[600] }}
                      >
                        Privacy Policy
                      </a>
                    </FormLabel>
                    <FormMessage />
                  </div>
                </FormItem>
              )} />

              <Button type="submit" className="w-full" disabled={isLoading || registerFlight.inFlight}>
                {isLoading ? "Creating account..." : "Create Advisor Account"}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter className="justify-center">
          <p className="text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link href="/login" className="text-primary hover:underline">Sign in</Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
